import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import ProductHero, { ProductContainer } from '../components/product/ProductHero';
import VideoGallery from '../components/VideoGallery';
import PhotoSlide from '../components/PhotoSlide';
import { Solutions } from '../assets/solutions';
import SeoHead from '../components/SeoHead';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN } from '../config/site';

const SolutionDetailPage = () => {
  const { solutionSlug } = useParams();
  const [solutionData, setSolutionData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadSolutionData = async () => {
      // Map slug to product ID
      const slugToId = {
        'sales': 1,
        'inventory': 2,
      };
      
      const solutionId = slugToId[solutionSlug];
      if (!solutionId) {
        setLoading(false);
        return;
      }
      
      // const product = await fetchProductData(productId);
      const solution = Solutions.find((product) => product.id === solutionId);
      setSolutionData(solution);
      setLoading(false);
    };

    loadSolutionData();
  }, [solutionSlug]);

  if (loading) {
    return <LoadingMessage>Loading product information...</LoadingMessage>;
  }

  if (!solutionData) {
    return <NotFoundPage />;
  }

  const ogImage = solutionData.backgroundImage
    ? `${SITE_ORIGIN}${solutionData.backgroundImage}`
    : undefined;
  
  return (
    <>
      <SeoHead
        title={solutionData.meta.title}
        description={solutionData.meta.description}
        keywords={solutionData.meta.keywords}
        path={`/solutions/${solutionSlug}`}
        image={ogImage}
      />
    <ProductContainer>      
      <ProductHero 
        product={solutionData}
      />
      {solutionData?.videos && solutionData?.videos?.length > 0 && (
        <VideoSection>
          <SectionTitle>Videos</SectionTitle>
          <VideoGallery videos={solutionData.videos} />
        </VideoSection>
      )}

      <FeaturesSection>
        <SectionTitle>Key Features</SectionTitle>
        <FeaturesGrid>
          {solutionData.mainFeatures.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureContent>
                <h3>{feature.title}</h3>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <div>
                  <PhotoSlide
                    images={feature.images}
                  />
                </div>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      {solutionData.benefits && (
        <BenefitsSection>
          <SectionTitle>Benefits for Your Business</SectionTitle>
          <BenefitsContainer>
            {solutionData.benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitNumber>{benefit.number}</BenefitNumber>
                <BenefitContent>
                  <h3>{benefit.title}</h3>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                  
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsContainer>
        </BenefitsSection>
      )}

      <AdditionalFeaturesSection>
        <SectionTitle>Additional Features</SectionTitle>
        <AdditionalFeaturesGrid>
          {solutionData.additionalFeatures.map((feature, index) => (
            <AdditionalFeatureCard key={index}>
              <AdditionalFeatureIcon>{feature.icon}</AdditionalFeatureIcon>
              <h3>{feature.title}</h3>
              {feature.description && <AdditionalFeatureDescription>{feature.description}</AdditionalFeatureDescription>}
            </AdditionalFeatureCard>
          ))}
        </AdditionalFeaturesGrid>
      </AdditionalFeaturesSection>

      <CTASection>
        <h2>Ready to transform your business?</h2>
        <p>Contact us today to schedule a personalized demo and learn how our solution can benefit your business.</p>
        <CTAButton to="/contact">Contact Us</CTAButton>
      </CTASection>
    </ProductContainer>
    </>
  );
};

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: var(--text-dark);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #d32f2f;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 2rem;
`;

const VideoSection = styled.section`
  margin: 1.2rem 0;
  text-align: center;
`;

const FeaturesSection = styled.section`
  margin: 2rem 0;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FeatureCard = styled.div`
  background-color: var(--primary-light);
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  background-color: var(--primary-color);
  color: white;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
    color: var(--primary-color);
  }
  
  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--text-dark);
  }
`;

const FeatureDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const AdditionalFeaturesSection = styled.section`
  margin: 2rem 0;
  text-align: center;
`;

const AdditionalFeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const AdditionalFeatureCard = styled.div`
  background-color: var(--primary-light);
  color: var(--text-dark);
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  position: relative;
  padding-top: 3rem;
  border: 1px solid var(--primary-color);
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: var(--primary-color);
  }
  
  p {
    font-size: 0.9rem;
    margin: 0;
  }
`;

const AdditionalFeatureIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 60px;
  background-color: var(--primary-color);
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const BenefitsSection = styled.section`
  margin: 2rem 0;
`;

const AdditionalFeatureDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const BenefitsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const BenefitNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--accent-color);
  line-height: 1;
`;

const BenefitContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
    color: var(--primary-color);
  }
  
  p {
    color: var(--text-dark);
    line-height: 1.6;
  }
`;

const BenefitDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const CTASection = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: 3rem;
  text-align: center;
  border-radius: 10px;
  margin-top: 2rem;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

export default SolutionDetailPage; 